<script setup lang="ts">
import { computed } from 'vue'
import { ElIcon } from 'element-plus'
import { Icon as IconifyIcon } from '@iconify/vue'
import { propTypes } from '@/utils/propTypes'
import { useDesign } from '@/hooks/web/useDesign'
import { ICON_PREFIX } from '@/constants'
import { ICON_REGISTRY } from './iconRegistry'
import type { IconDefinition } from './iconRegistry'

// 批量导入 Material Symbols SVG 文件
const materialSvgModules = import.meta.glob('/src/assets/icons/material/*.svg', {
  query: '?raw',
  import: 'default',
  eager: true
}) as Record<string, string>

// 构建 icon name -> SVG 内容 的映射
const materialSvgMap: Record<string, string> = {}
for (const [path, content] of Object.entries(materialSvgModules)) {
  const name = path.split('/').pop()!.replace('.svg', '')
  materialSvgMap[name] = content
}

const { getPrefixCls } = useDesign()

const prefixCls = getPrefixCls('icon')

const props = defineProps({
  name: propTypes.string.def(''),
  icon: propTypes.string.def(''),
  plain: propTypes.bool.def(false),
  color: propTypes.string,
  size: propTypes.number.def(16),
  hoverColor: propTypes.string
})

// ---------------------------------------------------------------------------
// 归一化输入
// ---------------------------------------------------------------------------

const effectiveIcon = computed<string>(() => (props.name || props.icon || '') as string)

// ---------------------------------------------------------------------------
// 注册表查找
// ---------------------------------------------------------------------------

const iconDef = computed<IconDefinition | null>(() => {
  if (props.name && ICON_REGISTRY[props.name as string]) return ICON_REGISTRY[props.name as string]
  if (props.icon && ICON_REGISTRY[props.icon as string]) return ICON_REGISTRY[props.icon as string]
  return null
})

// ---------------------------------------------------------------------------
// 渲染策略
// ---------------------------------------------------------------------------

const LEGACY_MATERIAL_NAMES = [
  'psychology',
  'library_books',
  'group',
  'admin_panel_settings',
  'smart_toy',
  'settings',
  'article',
  'menu',
  'person',
  'settings_suggest',
  'description'
]

const isMaterial = computed(
  () =>
    iconDef.value?.source === 'material-symbols' ||
    LEGACY_MATERIAL_NAMES.includes(effectiveIcon.value) ||
    (!iconDef.value &&
      !effectiveIcon.value.startsWith('svg-icon:') &&
      !effectiveIcon.value.startsWith(ICON_PREFIX) &&
      effectiveIcon.value.length > 0)
)

const isLocalSvg = computed(
  () => iconDef.value?.source === 'local-svg' || effectiveIcon.value.startsWith('svg-icon:')
)

const isIconifyMode = computed(() => !iconDef.value && effectiveIcon.value.startsWith(ICON_PREFIX))

const isUseOnline = computed(() => import.meta.env.VITE_USE_ONLINE_ICON === 'true')

// ---------------------------------------------------------------------------
// 渲染名
// ---------------------------------------------------------------------------

const renderName = computed<string>(() => {
  if (iconDef.value) return iconDef.value.rawName
  if (effectiveIcon.value.startsWith('svg-icon:'))
    return effectiveIcon.value.split('svg-icon:')[1] || ''
  if (effectiveIcon.value.startsWith(ICON_PREFIX))
    return effectiveIcon.value.replace(ICON_PREFIX, '')
  return effectiveIcon.value
})

const svgSymbolId = computed(() => `#icon-${renderName.value}`)

const iconifyStyle = computed(() => ({
  ...(props.plain ? {} : { fontSize: `${props.size}px` }),
  color: props.color as string | undefined
}))

// Material Symbols SVG 内联样式：plain 模式下不设 fontSize，由外部 class 控制尺寸
const materialStyle = computed(() => ({
  ...(props.plain ? {} : { fontSize: `${props.size}px` }),
  color: (props.color as string) || undefined
}))

// 获取当前 Material Symbol 的 SVG 内容
const materialSvgContent = computed(() => materialSvgMap[renderName.value] || '')

const hovercolorval = computed(() => props.hoverColor)
</script>

<template>
  <!-- ============================================================
       Plain 模式：直接渲染，无 ElIcon 包裹
       ============================================================ -->
  <template v-if="plain">
    <svg v-if="isLocalSvg" aria-hidden="true" class="inline-block">
      <use :href="svgSymbolId" />
    </svg>

    <span
      v-else-if="isMaterial && materialSvgContent"
      class="material-svg-icon"
      :style="materialStyle"
      v-html="materialSvgContent"
    ></span>

    <IconifyIcon
      v-else-if="isUseOnline && isIconifyMode && renderName"
      :icon="renderName"
      :style="iconifyStyle"
    />
    <span
      v-else-if="effectiveIcon"
      :class="`${effectiveIcon} iconify`"
      :style="iconifyStyle"
    ></span>
  </template>

  <!-- ===========================================================
       Wrapped 模式：ElIcon 包裹（菜单 / 表单）
       ============================================================ -->
  <ElIcon v-else :class="prefixCls" :size="size" :color="color">
    <svg v-if="isLocalSvg" aria-hidden="true">
      <use :href="svgSymbolId" />
    </svg>

    <span
      v-else-if="isMaterial && materialSvgContent"
      class="material-svg-icon"
      :style="materialStyle"
      v-html="materialSvgContent"
    ></span>

    <IconifyIcon
      v-else-if="isUseOnline && isIconifyMode && renderName"
      :icon="renderName"
      :style="iconifyStyle"
    />

    <span
      v-else-if="effectiveIcon"
      :class="`${effectiveIcon} iconify`"
      :style="iconifyStyle"
    ></span>
  </ElIcon>
</template>

<style lang="less" scoped>
@prefix-cls: ~'@{adminNamespace}-icon';

.@{prefix-cls},
.iconify {
  :deep(svg) {
    &:hover {
      color: v-bind(hovercolorval) !important;
    }
  }
}

.iconify {
  &:hover {
    color: v-bind(hovercolorval) !important;
  }
}

// Material SVG 图标样式
.material-svg-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1em;
  height: 1em;
  vertical-align: -0.125em;

  :deep(svg) {
    width: 100%;
    height: 100%;
    fill: currentcolor;
  }
}
</style>
